import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/firebaseConfig';
import { ref, set, onValue, remove } from 'firebase/database';
import { generateId } from '../firebase/idGenerator';
import './Report.css';

function Report() {
  const { currentUser, userProfile } = useAuth();
  const [category, setCategory] = useState("profile_issue");
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [myReports, setMyReports] = useState([]);

  // Fetch user's reports for realtime tracking
  useEffect(() => {
    if (!currentUser) return;
    const reportsRef = ref(db, 'reports');
    const unsubscribe = onValue(reportsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const filtered = Object.values(data)
          .filter(r => r.uid === currentUser.uid)
          .sort((a, b) => b.createdAt - a.createdAt);
        setMyReports(filtered);
      }
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return setError("Authentication required for neural reporting.");
    
    setLoading(true);
    setError("");

    try {
      const reportId = await generateId('RPT');
      const reportData = {
        reportId,
        uid: currentUser.uid,
        userName: userProfile?.name || "Anonymous",
        userEmail: currentUser.email,
        suid: userProfile?.suid || "N/A",
        category,
        subject,
        details,
        status: "open",
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      await set(ref(db, `reports/${reportId}`), reportData);
      setSubmittedId(reportId);
      setSubmitted(true);
      // Reset form
      setSubject("");
      setDetails("");
    } catch (err) {
      console.error("Failed to submit report", err);
      setError("Transmission error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (reportId) => {
    if (window.confirm("Are you sure you want to withdraw this inquiry? This action is irreversible.")) {
      try {
        await remove(ref(db, `reports/${reportId}`));
      } catch (err) {
        alert("Failed to withdraw report.");
      }
    }
  };

  const getPriorityInfo = () => {
    switch(category) {
      case 'suspension_appeal': return { label: 'CRITICAL', color: '#ff375f' };
      case 'profile_issue': return { label: 'HIGH_PRIORITY', color: 'var(--brand-accent)' };
      case 'technical_bug': return { label: 'STANDARD', color: '#a5b4fc' };
      default: return { label: 'ROUTINE', color: '#8b949e' };
    }
  };

  if (submitted) {
    return (
      <div className="report-container animate-up">
        <div className="report-card text-center">
          <div className="success-icon">
            <i className="fa-solid fa-circle-check"></i>
          </div>
          <h2 className="success-title-v3">Report Filed</h2>
          <div className="case-id-box-v3">
            <span className="case-label-v3">Neural Tracking ID</span>
            <strong className="case-id-v3">{submittedId}</strong>
          </div>
          <p className="success-text-v3">Transmission acknowledged. Your inquiry is currently triaged under <strong>Neural Oversight</strong> protocols.</p>
          <div className="success-actions">
            <button className="btn btn-primary" style={{width: '100%'}} onClick={() => setSubmitted(false)}>File Another Inquiry</button>
            <button className="btn btn-secondary" style={{width: '100%', marginTop: '10px'}} onClick={() => window.history.back()}>Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-container animate-up">
      <div className="oversight-monolith">
        <div className="report-card filing-terminal">
          <div className="terminal-header">
            <div className="scanning-line"></div>
            <h1>Neural Oversight // Filing Terminal</h1>
            <p>Submit a formal inquiry for manual audit. High-entropy reports are prioritized.</p>
          </div>

          {error && <div className="error-pill"><i className="fa-solid fa-triangle-exclamation"></i> {error}</div>}

          <form onSubmit={handleSubmit} className="custom-report-v3">
            <div className="form-row-v3">
              <div className="form-group-v3">
                <label>Classification</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} required>
                  <option value="profile_issue">Identity / Profile Anomaly</option>
                  <option value="suspension_appeal">Restriction Appeal</option>
                  <option value="technical_bug">Architectural Bug</option>
                  <option value="content_complaint">Content Infringement</option>
                  <option value="other">General Inquiry</option>
                </select>
              </div>
              <div className="form-group-v3">
                <label>Status Priority</label>
                <div className="priority-readout" style={{color: getPriorityInfo().color}}>
                  <i className="fa-solid fa-bolt"></i> {getPriorityInfo().label}
                </div>
              </div>
            </div>

            <div className="form-group-v3">
              <label>Subject Headline</label>
              <input type="text" placeholder="Summary of inquiry..." value={subject} onChange={(e) => setSubject(e.target.value)} required />
            </div>

            <div className="form-group-v3">
              <label>Neural Context (Breifing)</label>
              <textarea placeholder="Provide comprehensive details for audit..." value={details} onChange={(e) => setDetails(e.target.value)} required></textarea>
            </div>

            <button disabled={loading} type="submit" className="btn btn-primary btn-terminal">
              {loading ? 'TRANSMITTING...' : 'INITIATE FILING'}
            </button>
          </form>
        </div>

        {/* TRACKING TERMINAL */}
        <div className="tracking-terminal glass-card">
          <h3 className="tracking-title"><i className="fa-solid fa-folder-open"></i> Active Audits ({myReports.length})</h3>
          <div className="tracking-list">
            {myReports.length > 0 ? myReports.map(report => (
              <div key={report.reportId} className={`tracking-item status-${report.status}`}>
                <div className="item-main">
                  <div className="item-meta">
                    <span className="item-id">{report.reportId}</span>
                    <span className="item-date">{new Date(report.createdAt).toLocaleDateString()}</span>
                  </div>
                  <h4 className="item-subject">{report.subject}</h4>
                  <p className="item-category">{report.category.replace('_', ' ').toUpperCase()}</p>
                </div>
                <div className="item-aside">
                  <span className={`status-tag badge-${report.status}`}>{report.status.toUpperCase()}</span>
                  {report.status === 'open' && (
                    <button className="withdraw-btn" onClick={() => handleWithdraw(report.reportId)}>
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  )}
                </div>
              </div>
            )) : (
              <div className="empty-tracking">
                <i className="fa-solid fa-inbox"></i>
                <p>No active inquiries found in neural storage.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Report;
